import React, { useState, useEffect } from 'react';
import * as style from './squorli-widget.module.scss';

const SQUORLI_PATH =
  'M89 270 C73 247 94 172 127 136 L124 102 Q121 80 143 91 L168 104 C205 86 276 77 312 88 C352 100 371 133 366 171 C363 200 346 224 320 230 C291 238 270 225 244 213 C207 196 173 205 146 226 C122 245 107 262 98 272 Q93 278 89 270 Z M285 117 C291 118 292 125 290 136 C288 147 284 153 278 152 C272 151 271 144 273 133 C275 122 279 116 285 117 Z M319 130 C325 131 326 138 324 149 C322 160 318 166 312 165 C306 164 305 157 307 146 C309 135 313 129 319 130 Z';

const SquorliLogo = ({ color = '#fff', width = 48, height = 48 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 512 512"
    width={width}
    height={height}
  >
    <g fill={color} fillRule="evenodd">
      {[0, 120, 240].map(angle => (
        <path
          key={angle}
          transform={`rotate(${angle} 256 256) translate(-5 -17) translate(223 168) scale(.85) translate(-223 -168)`}
          d={SQUORLI_PATH}
        />
      ))}
    </g>
  </svg>
);

const MicMutedIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 484.5 484.5"
    width="10"
    height="10"
    className={style.mutedButton}
  >
    <path d="M420.75 229.5H377.4c0 17.85-5.101 35.7-10.2 51l30.6 30.6c15.3-22.95 22.95-51 22.95-81.6zM318.75 234.6V76.5c0-43.35-33.15-76.5-76.5-76.5s-76.5 33.15-76.5 76.5v5.1l153 153zM45.9 25.5L12.75 58.65l153 153v17.85c0 43.35 33.15 76.5 76.5 76.5 5.1 0 10.2 0 17.85-2.55l43.351 43.35c-17.851 7.65-38.25 12.75-58.65 12.75-71.4 0-135.15-53.55-135.15-130.05h-45.9c0 86.7 68.85 158.1 153 170.85v84.15h51v-84.15c22.95-2.55 45.9-12.75 63.75-22.949l107.1 107.1 33.15-33.15L45.9 25.5z" />
  </svg>
);

const SoundMutedIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 461.55 461.55"
    width="10"
    height="10"
    className={style.mutedButton}
  >
    <path d="M345.525 229.5c0-45.9-25.5-84.15-63.75-102v56.1l63.75 63.75V229.5zm63.75 0c0 22.95-5.1 45.9-12.75 66.3l38.25 38.25c17.85-30.6 25.5-68.85 25.5-107.1 0-109.65-76.5-201.45-178.5-224.4V56.1c73.95 25.5 127.5 91.8 127.5 173.4zM34.425 0L1.275 33.15 121.125 153H1.275v153h102l127.5 127.5V262.65l109.65 109.65c-17.851 12.75-35.7 22.95-58.65 30.601v53.55c35.7-7.65 66.3-22.95 94.35-45.9l51 51 33.15-33.149-229.5-229.5L34.425 0zm196.35 25.5l-53.55 53.55 53.55 53.55V25.5z" />
  </svg>
);

const initials = name =>
  name
    .split(/\s+/)
    .map(part => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

// deterministische Farbe aus dem Namen, damit sie über Reloads stabil bleibt
const nameColor = name => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) % 360;
  }
  return `hsl(${hash}, 60%, 40%)`;
};

const SquorliWidget = ({ url, apiKey, join, open, onToggle }) => {
  const [squorliData, setSquorliData] = useState({});
  const [panelOpen, setPanelOpen] = useState(false);

  // von außen steuerbar (VoiceWidgets), fällt sonst auf eigenen State zurück
  const isOpen = typeof open === 'boolean' ? open : panelOpen;

  const apiUrl = String(url || '').replace(/\/+$/, '');

  /**
   * fetches Data from the Squorli status api
   */
  const fetchData = async () => {
    try {
      const query = apiKey ? `?key=${encodeURIComponent(apiKey)}` : '';
      const response = await fetch(`${apiUrl}/api/status${query}`, {
        cache: 'no-cache',
      });
      if (!response.ok) return;

      setSquorliData(await response.json());
    } catch (error) {
      // Server nicht erreichbar — letzte bekannte Daten behalten
    }
  };

  const createMember = ({ userId, displayName, avatarUrl, afk, voice }) => (
    <li key={`member-${userId}`} className={style.flex}>
      <div
        className={`${style.imageWrapper} ${afk ? style.idle : style.online}`}
      >
        {avatarUrl ? (
          <img className={style.avatar} src={avatarUrl} alt={displayName} />
        ) : (
          <span
            className={`${style.avatar} ${style.initials}`}
            style={{ background: nameColor(displayName) }}
          >
            {initials(displayName)}
          </span>
        )}
      </div>
      <div className={style.userWrapper}>
        <div className={style.userName} title={displayName}>
          {displayName}
        </div>
      </div>
      {voice.deafened && (
        <span className={style.muteWrapper} title="Lautsprecher aus">
          <SoundMutedIcon />
        </span>
      )}
      {voice.micMuted && (
        <span className={style.muteWrapper} title="Mikrofon aus">
          <MicMutedIcon />
        </span>
      )}
    </li>
  );

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { categories = [], channels = [], members = [] } = squorliData;

  const voiceMembers = members.filter(({ voice }) => Boolean(voice));

  // Voice-Channels in der Reihenfolge aus Squorli: erst Kategorie, dann Channel
  const categoryPosition = id => {
    const category = categories.find(cat => cat.id === id);
    return category ? category.position : -1;
  };
  const voiceChannels = channels
    .filter(({ kind }) => kind === 'voice')
    .sort(
      (a, b) =>
        categoryPosition(a.categoryId) - categoryPosition(b.categoryId) ||
        a.position - b.position
    );

  return (
    <div className={style.squorliWrapper} data-voice-widget="">
      <button
        className={style.toggleButton}
        onClick={() => {
          if (!isOpen) {
            fetchData();
          }

          if (onToggle) {
            onToggle(!isOpen);
          } else {
            setPanelOpen(!isOpen);
          }
        }}
      >
        <SquorliLogo width={40} height={40} />
        <span className={style.online}>{voiceMembers.length}</span>
      </button>
      <div
        className={style.squorliWidget}
        style={{ display: isOpen ? 'block' : 'none' }}
      >
        <h3>
          {Boolean(join) ? (
            <a
              href={join}
              title="join"
              target="_blank"
              rel="noopener noreferrer"
            >
              <SquorliLogo width={18} height={18} /> Squorli
            </a>
          ) : (
            <>
              <SquorliLogo width={18} height={18} /> Squorli
            </>
          )}
        </h3>
        <ul className="members">
          {voiceChannels.map(({ id, name }) => {
            const channelMember = voiceMembers.filter(
              ({ voice }) => voice.channelId === id
            );
            return (
              <li key={`channel-${id}`}>
                <h4>{name}</h4>
                <ul>
                  {channelMember.length ? (
                    channelMember.map(createMember)
                  ) : (
                    <li>Niemand hier</li>
                  )}
                </ul>
                <hr style={{ borderColor: 'white' }} />
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export { SquorliWidget, SquorliLogo };
